import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  useDeleteGoalMutation,
  useGoal,
  useGoals,
  useUpdateGoalMutation,
} from '../../../api/strategy-client/strategy-client.hooks';
import { useTranslation } from 'react-i18next';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { PageLayout } from '../../../components/page-layout/page-layout';
import { useDocumentTitle } from '../../../helpers/general-helpers';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import {
  setInitiativePerformance,
  setPerformanceActivePanel,
  setPerformanceTab,
} from '../../../store/strategy-store/strategy-store.actions';
import { Panel } from '../../../store/strategy-store/strategy-store.type';
import { useFlora } from '../../flora/flora.context';
import { FloraSupportedObjectTypes } from '../../flora/flora.type';
import { MenuDrivenContainer } from '../../menu-driven-container/menu-driven-container';
import { toast } from '../../notifications';
import { GeneralViewNames } from '../../side-bar/side-bar.type';
import { MainSection as MainSectionCost } from '../initiative-performance/cost-tab/main-section/main-section';
import { MainSection as MainSectionFocus } from '../initiative-performance/focus-tab/main-section/main-section';
import { InitiativePerformanceFilters } from '../initiative-performance/initiative-performance-filters';
import { InitiativePerformanceSideSection } from '../initiative-performance/initiative-performance-side-section';
import { useSideSection } from '../initiative-performance/initiative-performance.hooks';
import { PerformanceTab } from '../initiative-performance/initiative-performance.type';
import { MainSection as MainSectionRelatedTasks } from '../initiative-performance/related-tasks-tab/main-section/main-section';
import { MainSection as MainSectionThroughput } from '../initiative-performance/throughput-tab/main-section/main-section';
import { StrategyEntityModal } from '../strategy-entity-modal/strategy-entity-modal';
import { StrategyPerformanceControlBar } from '../strategy-performance/strategy-performance-control-bar';
import { StrategyPerformanceHeader } from '../strategy-performance/strategy-performance-header';
import { StrategyPerformanceInfoModal } from '../strategy-performance/strategy-performance-info-modal';
import { StrategyPerformanceOverview } from '../strategy-performance/strategy-performance-overview';
import { StrategyPerformanceTabs } from '../strategy-performance/strategy-performance-tabs';
import { transformGoalToInitiativeFormat } from '../strategy-performance/strategy-performance.hooks';
import { StrategyEntityType } from '../strategy.type';

export function GoalDetails() {
  useDocumentTitle('Goal Details - Bloomfilter');

  const i18n = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { goal, query } = useGoal(id);
  const { query: goalsQuery } = useGoals();
  const performanceTab = useStrategyStore((state) => state.performanceTab);
  const performanceActivePanel = useStrategyStore((state) => state.performanceActivePanel);
  const showSideSection = useSideSection();
  const { setContextualInfo } = useFlora();

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const deleteGoalMutation = useDeleteGoalMutation({
    onSuccess: () => {
      setIsEditModalOpen(false);
      goalsQuery.refetch();
      navigate('/application/strategy/goals');
      toast.success(`${i18n.t('common.strategic_goal')} successfully deleted`);
    },
    onError: (error) => {
      toast.error(
        `Failed to delete ${i18n.t('common.strategic_goal').toLowerCase()}: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    },
  });

  const updateGoalMutation = useUpdateGoalMutation({
    onSuccess: (data) => {
      setIsEditModalOpen(false);
      query.refetch();
      toast.success(`${i18n.t('common.strategic_goal')} "${data.name}" updated successfully`);
    },
    onError: (error) => {
      toast.error(
        `Failed to update ${i18n.t('common.strategic_goal').toLowerCase()}: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    },
  });

  useEffect(() => {
    // Set default tab to throughput
    setPerformanceTab(PerformanceTab.Throughput);
    setPerformanceActivePanel(Panel.Filters);
  }, []);

  useEffect(() => {
    if (goal) {
      setContextualInfo({
        currentModule: GeneralViewNames.Strategy,
        currentObjects: [{ type: FloraSupportedObjectTypes.StrategicGoal, id: goal.id, name: goal.name }],
        startDate: goal.start_date ?? null,
        endDate: goal.end_date ?? null,
      });
    }
  }, [goal, setContextualInfo]);

  useEffect(() => {
    if (query.error) {
      navigate('/application/strategy/goals');
    }
  }, [query.error, navigate]);

  // Transform and store goal data for charts
  useEffect(() => {
    if (goal) {
      const transformed = transformGoalToInitiativeFormat(goal);
      setInitiativePerformance(transformed);
    }
    return () => {
      setInitiativePerformance(null);
    };
  }, [goal]);

  const showSideFilters = performanceActivePanel === Panel.Filters;
  const setShowSideFilters = (show: boolean) => {
    setPerformanceActivePanel(show ? Panel.Filters : null);
  };

  const renderMainSection = () => {
    switch (performanceTab) {
      case PerformanceTab.Throughput:
        return <MainSectionThroughput />;
      case PerformanceTab.Focus:
        return <MainSectionFocus />;
      case PerformanceTab.Cost:
        return <MainSectionCost />;
      case PerformanceTab.RelatedTasks:
        return <MainSectionRelatedTasks />;
      default:
        return <MainSectionThroughput />;
    }
  };

  const handleUpdate = async (data: any) => {
    if (!goal) return;
    await updateGoalMutation.mutateAsync({ goalId: goal.id, data });
  };

  return (
    <MenuDrivenContainer
      header={
        <StrategyPerformanceHeader
          entity={goal ?? null}
          entityType={StrategyEntityType.Goal}
          onUpdate={() => query.refetch()}
        />
      }
    >
      <BrandedLoadingOverlay visible={query.isLoading} variant="colored" transitionDuration={30} />
      <PageLayout
        controlBar={
          <StrategyPerformanceControlBar
            entity={goal ?? null}
            entityType="goal"
            openInfoModal={setIsInfoModalOpen}
            openEditModal={setIsEditModalOpen}
          />
        }
        overview={{
          title: i18n.t('strategy.overview.title'),
          content: <StrategyPerformanceOverview entity={goal ?? null} entityType="goal" />,
        }}
        tabs={<StrategyPerformanceTabs entityType={StrategyEntityType.Goal} />}
        sideFilters={{
          title: i18n.t('common.filters'),
          content: <InitiativePerformanceFilters setShow={setShowSideFilters} />,
          show: showSideFilters,
          setShow: setShowSideFilters,
        }}
        mainSection={renderMainSection()}
        sideSection={<InitiativePerformanceSideSection />}
        showSideSection={showSideSection}
        data-page-layout
      />
      <StrategyPerformanceInfoModal
        entity={goal ?? null}
        opened={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
      />
      {goal && (
        <StrategyEntityModal
          mode="edit"
          entityType={StrategyEntityType.Goal}
          entity={goal}
          opened={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleUpdate}
          isSubmitting={updateGoalMutation.isPending}
          onDelete={() => deleteGoalMutation.mutateAsync(goal.id)}
          isDeleting={deleteGoalMutation.isPending}
        />
      )}
    </MenuDrivenContainer>
  );
}
